import { isAxiosError, type AxiosError } from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Marks a request authenticated with a step-up (MFA) bearer token rather than the ordinary session. Set by
     * `axiosSteppedUpClient`; read by `isSessionExpiredResponse`.
     */
    isStepUpRequest?: boolean;
  }
}

type CustomerAuthenticationPath = "login" | "signup";

/**
 * Builds an authentication URL for the current page, so the user returns after authentication.
 *
 * This uses the browser location instead of the failed request URL. An expired session can be detected through an API
 * request, and returning the user to that URL would fail again.
 */
export const getAuthenticationUrl = (customerAuthenticationPath: CustomerAuthenticationPath) => {
  const { pathname, search } = window.location;
  // Mirror the backend's staff/customer split (see core.routing.get_base_login_url).
  const authenticationPath = pathname.startsWith("/staff")
    ? "/authn/login-staff"
    : `/authn/${customerAuthenticationPath}`;
  // Never point `next` back at an AuthN page. Those are transient (`/authn-wf/callback` and friends), so returning
  // the user to one after authentication lands them somewhere they cannot use. Omitting `next` lets AuthN fall back
  // to its own post-authentication destination, matching the backend's `login_url(redirect_to_request_url=False)`.
  if (pathname.startsWith("/authn")) {
    return authenticationPath;
  }
  return `${authenticationPath}?next=${encodeURIComponent(pathname + search)}`;
};

export const getLoginUrl = () => getAuthenticationUrl("login");

/** Sends the user to authentication and returns them to the current page afterwards. */
export const redirectToAuthentication = (authenticationPath: CustomerAuthenticationPath = "login") => {
  // Already on an AuthN page - don't bounce at all. `getAuthenticationUrl` keeps `next` safe for callers that still
  // need a URL here (the session-expired page's CTA), but an automatic navigation would reload the page we are on.
  if (window.location.pathname.startsWith("/authn")) {
    return;
  }
  window.location.assign(getAuthenticationUrl(authenticationPath));
};

export const redirectToLogin = () => redirectToAuthentication();

/**
 * Detects when the server bounced an XHR/API request to an HTML login page - i.e. axios transparently followed a 302
 * to `/authn/...` and resolved with the login *document* - and sends the user to login. Left alone, the caller would
 * try to parse that HTML as JSON.
 *
 * This only runs for *fulfilled* responses (it's wired into the response interceptor's success path). Rejected
 * responses go to `handleAuthenticationFailureError` instead.
 *
 * @param response - A fulfilled axios response object
 * @throws Error if the server redirected the request to the login page
 */
export const handleAuthenticationFailure = (response: any) => {
  // Check if server redirected API request to login page (HTML response)
  const isHtmlResponse = response?.headers?.["content-type"]?.includes?.("text/html");

  if (isHtmlResponse) {
    const { pathname } = response.request?.responseURL ? new URL(response.request.responseURL) : { pathname: "" };

    if (pathname.startsWith("/authn/")) {
      redirectToLogin();
      throw new Error("Server redirected API request to login page, most likely due to expired session");
    }
  }
};

/**
 * The `typ` discriminator from `wf.authz` for a token whose `exp` claim has passed.
 *
 * Deliberately *only* this one. Its parent class `AuthzTokenError` - raised for a token that is missing, malformed
 * or badly signed - is excluded on purpose: it does not tell us a session ever existed, so showing "your session has
 * expired" for it would assert something we cannot know. Those keep falling through to "Access denied", as do
 * genuine authorization failures (`AuthzPermissionError`, `AuthzCompanyError`, a bare `AuthzError`).
 */
const SESSION_EXPIRED_TYP = "AuthzTokenExpiredError";

/**
 * Whether an axios rejection is an expired session rather than a genuine authorization failure.
 *
 * The error is left as-is rather than converted into a bespoke type: plenty of code keys off the axios shape
 * (`is4XXAxiosError` for log levels, `error.response?.status` in components), and replacing it makes those checks
 * silently stop matching. Consumers that need the session-expired *page* use `getErrorVariant` from `shared-ui`.
 *
 * Both statuses are checked because the backend is inconsistent: `wf.ninja` API routes return 403, since the
 * `AuthzError` handler in `core/errors.py` catches token errors before their own 401 status is ever read, while
 * routes served by `AuthzErrorMiddleware` return the library's own 401. `typ` is what actually discriminates - the
 * status alone cannot.
 */
export const isSessionExpiredError = (error: unknown): error is AxiosError => {
  if (!isAxiosError(error)) {
    return false;
  }
  // A step-up (MFA) token expiring is not the session expiring - the ordinary session is usually still valid, and the
  // fix is to re-run MFA via `authn.stepUp()`, not to log in again. `StepUpMfaGuard` owns that flow, driven by local
  // token state. Sending these to the session-expired page would offer a full re-login the user does not need.
  if (error.config?.isStepUpRequest) {
    return false;
  }
  const { status, data } = error.response ?? {};
  if (status !== 401 && status !== 403) {
    return false;
  }
  return data?.typ === SESSION_EXPIRED_TYP;
};
